import { lazy, Suspense } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { RichText } from '../components/coach/rich-text';
import { FrequencyPlayer } from '../components/frequency-player';
import { linkButton } from '../components/panels/today';
import {
  Block,
  CoachButton,
  ErrorNote,
  Loading,
  SageCheck,
  Screen,
} from '../components/primitives';
import { ProLock } from '../components/pro-lock';
import { apiErrorPayload, coaching, type FrequencyTrack } from '../lib/api';
import { longDay } from '../lib/format';
import { qk, useFeature, useProgramDay, useRefreshCoaching, useToday } from '../lib/queries';

// The video player is the same lazy chunk the course player pays for.
const ProgramVideo = lazy(() =>
  import('../components/program-video').then((m) => ({ default: m.ProgramVideo })),
);

/**
 * One day of a program: the read, the way to Master Kala with the day's prompt
 * already in the box, the micro-action as it stands on the plan, and "done".
 * `/coaching/today` is awaited first because that call creates the day's
 * action; the day is read only after it, so the action is never missing here.
 */
export function ProgramDayPage() {
  const { id, dayNo } = useParams();
  const n = Number(dayNo);
  const navigate = useNavigate();
  const qc = useQueryClient();
  const state = useFeature('programs');
  const today = useToday();
  const day = useProgramDay(id!, n, today.isSuccess);
  const refresh = useRefreshCoaching();

  const complete = useMutation({
    mutationFn: () => coaching.completeProgramDay(id!, n),
    onSuccess: (res) => {
      coaching.event('program_day_completed', { program: id!, day: n });
      if (res.programCompleted) coaching.event('program_completed', { program: id! });
      qc.setQueryData(qk.programDay(id!, n), res.day);
      void refresh();
    },
  });

  if (state === 'off') return <Navigate to="/achieve" replace />;
  if (state === 'locked') {
    return (
      <Screen back={`/programs/${id}`} eyebrow="Program" title="Guided programs">
        <ProLock feature="programs" />
      </Screen>
    );
  }
  if (today.isPending || day.isPending) return <Loading />;
  if (day.isError) {
    const payload = apiErrorPayload(day.error) as { error?: string; unlocksOn?: string } | null;
    if (payload?.error === 'program_day_locked') {
      return (
        <Screen back={`/programs/${id}`} eyebrow={`Day ${n}`} title="Not yet">
          <Block>
            {payload.unlocksOn
              ? `This day opens on ${longDay(payload.unlocksOn)}. One day at a time.`
              : 'This day is not open yet. One day at a time.'}
          </Block>
        </Screen>
      );
    }
    return <ErrorNote error={day.error} retry={() => void day.refetch()} />;
  }

  const d = day.data;
  const todayDay = today.data?.day ?? '';
  const actionLater = !d.action.id && d.action.dueOn > todayDay;
  const audioTrack: FrequencyTrack | null =
    d.media?.kind === 'audio'
      ? {
          id: `${d.program.id}:${d.dayNo}`,
          title: d.title,
          description: '',
          category: 'program',
          frequencyLabel: null,
          durationSeconds: null,
          premiumLevel: 'free',
        }
      : null;

  return (
    <Screen
      back={`/programs/${id}`}
      eyebrow={`Day ${d.dayNo} of ${d.program.daysCount} · ${d.program.title}`}
      title={d.title}
    >
      {audioTrack && (
        <section className="border-b border-border px-5 py-6">
          <FrequencyPlayer
            track={audioTrack}
            compact
            source={{
              key: qk.programStream(d.program.id, d.dayNo),
              fetch: () => coaching.programStream(d.program.id, d.dayNo),
            }}
          />
        </section>
      )}
      {d.media?.kind === 'video' && (
        <Suspense fallback={<Loading label="Loading the video" />}>
          <ProgramVideo src={d.media.ref} title={d.title} />
        </Suspense>
      )}
      <section className="border-b border-border px-5 py-6 text-[0.95rem] leading-relaxed text-ink/85 lg:py-8">
        <RichText text={d.lesson} />
      </section>
      <Block
        eyebrow="Ask Master Kala"
        title="Take this day to your coach"
        action={
          <CoachButton onClick={() => navigate('/coach', { state: { draft: d.kalaPrompt } })}>
            Ask Master Kala
          </CoachButton>
        }
      >
        {d.kalaPrompt}
      </Block>
      <Block
        eyebrow="Today’s action"
        title={d.action.title}
        action={
          d.action.id ? (
            <Link to="/achieve/actions" className={linkButton}>
              {d.action.status === 'done' ? 'Done' : 'Open my actions'}
            </Link>
          ) : actionLater ? (
            <p className="text-sm leading-relaxed text-ink/70">
              Set for {longDay(d.action.dueOn)}. It lands on your plan that morning.
            </p>
          ) : (
            <p className="text-sm leading-relaxed text-ink/70">
              This day’s action was for {longDay(d.action.dueOn)}. Read the day; the plan moves on.
            </p>
          )
        }
      >
        {d.action.minutes ? `About ${d.action.minutes} minutes.` : null}
      </Block>
      <section className="border-b border-border px-5 py-6">
        {d.lessonDoneAt ? (
          <>
            <p className="flex items-center gap-2 text-sm text-sage">
              <SageCheck size={18} />
              {d.next
                ? `Marked done. Next: Day ${d.next.dayNo}, ${d.next.title}.`
                : 'Marked done. That was the last day.'}
            </p>
            <Link to={`/programs/${id}`} className="eyebrow mt-4 inline-block hover:text-ink">
              All days
            </Link>
          </>
        ) : (
          <CoachButton kind="gold" disabled={complete.isPending} onClick={() => complete.mutate()}>
            Done with today’s read
          </CoachButton>
        )}
      </section>
    </Screen>
  );
}
